import {
  Avatar,
  Button,
  Col,
  Dropdown,
  Flex,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography
} from 'antd';
import React, { useMemo, useState } from 'react';
import {
  CheckCircleFilled,
  InfoCircleFilled,
  UserOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import FullScreenModal from 'src/components/layout/FullScreenModal';
import OutreachResults from 'src/components/project/search/OutreachResults';
import {
  BoxColumn,
  ViabilityIconBox
} from 'src/components/project/explore/summary/helpers';
import { locationRender } from 'src/pages/rfq/proposal/supply/SupplyRoute';
import { QuoteDetails } from 'src/components/project/search/QuoteDetails';
import useIsConcierge from 'src/hooks/useIsConcierge';
import useLoadingStateClass from 'src/hooks/useLoadingStateClass';
import useUpdateProject from 'src/Mutation/useUpdateProject';
import useGetPopulatedProject from 'src/Query/useGetPopulatedProject';

export default function ProposalModal() {
  const navigate = useNavigate();
  const isConcierge = useIsConcierge();
  const [searchParams] = useSearchParams();
  const projectUuid = searchParams.get('project');

  const [showingTds, setShowingTds] = useState(null);

  const {
    data: project,
    isLoading,
    isRefetching
  } = useGetPopulatedProject(projectUuid);

  const { settings } = project ?? {};

  const {
    mutate,
    isLoading: isUpdatingProject,
    messageContext
  } = useUpdateProject(projectUuid);

  const loadingClass = useLoadingStateClass(isUpdatingProject || isRefetching);

  const tdss = useMemo(() => (project ? project.tdss : []), [project]);

  const editableConfig = useMemo(
    () => (key) =>
      !isConcierge
        ? false
        : {
            onChange: (val) =>
              mutate({
                uuid: projectUuid,
                settings: {
                  ...(settings ?? {}),
                  [key]: val
                }
              })
          },
    [isConcierge, settings]
  );

  if (isLoading) {
    return (
      <FullScreenModal
        title="Project Quotes"
        width="100%"
        open
        onBack={() => navigate(`/project/search?project=${projectUuid}`)}
        className={`search--proposal ${loadingClass}`}
        headerProps={{
          right: (
            <Link to={`/digital-rfq/${projectUuid}`}>
              <Button title="View Digital RFQ">View Digital RFQ</Button>
            </Link>
          )
        }}
      >
        <Skeleton />
      </FullScreenModal>
    );
  }

  return (
    <>
      {messageContext}
      <FullScreenModal
        title="Project Quotes"
        width="100%"
        open
        onBack={() => navigate(`/project/search?project=${projectUuid}`)}
        className="search--proposal"
        headerProps={{
          right: (
            <Link to={`/digital-rfq/${projectUuid}`}>
              <Button title="View Digital RFQ">View Digital RFQ</Button>
            </Link>
          )
        }}
      >
        <Flex
          align="start"
          className={loadingClass}
          direction="horizontal"
          justify="center"
        >
          <Flex
            className="search--proposal--list"
            justify={showingTds ? 'end' : 'center'}
          >
            <Space
              className="mr-xl"
              direction="vertical"
              style={{ maxWidth: '960px', minWidth: '865px' }}
            >
              <Typography.Title className="mb-lg mt-lg" level={3}>
                Project Quotes
              </Typography.Title>
              <div className="color-card color-card--info mb-lg">
                <Typography.Text editable={editableConfig('proposal_summary')}>
                  {settings?.proposal_summary ?? '(no content)'}
                </Typography.Text>
              </div>
              <OutreachResults tdss={tdss} />
              <Space className="quotes" direction="vertical" size={16}>
                <Row gutter={[16, 0]} justify="end">
                  <BoxColumn>
                    <Typography.Text type="secondary">Price</Typography.Text>
                  </BoxColumn>
                  <BoxColumn>
                    <Typography.Text type="secondary">Specs</Typography.Text>
                  </BoxColumn>
                  <BoxColumn>
                    <Typography.Text type="secondary">Supply</Typography.Text>
                  </BoxColumn>
                  <BoxColumn>
                    <Typography.Text type="secondary">Brand</Typography.Text>
                  </BoxColumn>
                </Row>
                {tdss
                  // For some reason the .map function is giving a `cannot read properties of undefined` if we don't filter?
                  .filter((tds) => !!tds.company)
                  .map((tds) => (
                    <Row
                      key={tds.uuid}
                      className="single-quote"
                      gutter={[16, 0]}
                      wrap={false}
                      onClick={() => setShowingTds(tds)}
                    >
                      <Col flex="1">
                        <Row gutter={[12, 0]} wrap={false} align="middle">
                          <Col>
                            <Avatar
                              icon={<UserOutlined />}
                              src={tds.company.avatar}
                            />
                          </Col>
                          <Col>
                            <div>
                              <Typography.Text
                                style={{ lineHeight: '20px' }}
                                strong
                              >
                                {tds.company.name}
                              </Typography.Text>
                              {!!settings?.submittedTdss?.[tds.uuid]?.tag && (
                                <Tag color="#2f54eb">
                                  {settings?.submittedTdss?.[tds.uuid]?.tag}
                                </Tag>
                              )}
                            </div>
                            <div>
                              <Typography.Text
                                style={{ fontSize: 12, lineHeight: '12px' }}
                                type="secondary"
                              >
                                {locationRender(tds.locations[0])}
                              </Typography.Text>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <BoxColumn>
                        <EditableMatchIcon
                          projectUuid={projectUuid}
                          settingsKey="price_match"
                          tdsUuid={tds.uuid}
                        />
                      </BoxColumn>
                      <BoxColumn>
                        <EditableMatchIcon
                          projectUuid={projectUuid}
                          settingsKey="specs_match"
                          tdsUuid={tds.uuid}
                        />
                      </BoxColumn>
                      <BoxColumn>
                        <EditableMatchIcon
                          projectUuid={projectUuid}
                          settingsKey="supply_match"
                          tdsUuid={tds.uuid}
                        />
                      </BoxColumn>
                      <BoxColumn>
                        <EditableMatchIcon
                          projectUuid={projectUuid}
                          settingsKey="brand_match"
                          tdsUuid={tds.uuid}
                        />
                      </BoxColumn>
                    </Row>
                  ))}
              </Space>
            </Space>
          </Flex>
          {!!showingTds && (
            <Flex align="start" className="search--proposal--details" vertical>
              <QuoteDetails
                onClose={() => setShowingTds(null)}
                project={project}
                tds={showingTds}
              />
            </Flex>
          )}
        </Flex>
      </FullScreenModal>
    </>
  );
}
ProposalModal.propTypes = {};

const matchOptions = [
  {
    key: 'match',
    label: 'Match',
    icon: (
      <CheckCircleFilled
        style={{
          color: '#389e0d'
        }}
      />
    )
  },

  {
    key: 'nomatch',
    label: 'No match',
    icon: (
      <InfoCircleFilled
        style={{
          color: '#faad14'
        }}
      />
    )
  }
];

function EditableMatchIcon({ projectUuid, settingsKey, tdsUuid }) {
  const { data: project } = useGetPopulatedProject(projectUuid);
  const { mutate, messageContext } = useUpdateProject(projectUuid);

  const { settings = {} } = project ?? {};

  const match = useMemo(
    () => settings?.submittedTdss?.[tdsUuid]?.[settingsKey] ?? false,
    [settings]
  );

  return (
    <>
      {messageContext}
      <Dropdown
        trigger="hover"
        menu={{
          items: matchOptions,
          onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();

            const updatedSettings = {
              ...settings,
              submittedTdss: {
                ...(settings?.submittedTdss ?? {}),
                [tdsUuid]: {
                  ...(settings?.submittedTdss?.[tdsUuid] ?? {}),
                  [settingsKey]: key === 'match'
                }
              }
            };

            mutate({
              uuid: projectUuid,
              settings: updatedSettings
            });
          }
        }}
      >
        <ViabilityIconBox recordsCount={match ? 5 : 0} />
      </Dropdown>
    </>
  );
}

EditableMatchIcon.propTypes = {
  projectUuid: PropTypes.string,
  settingsKey: PropTypes.string,
  tdsUuid: PropTypes.string
};
